'use client';

import { useEffect } from 'react';

import { configureCloudWatchSink } from '../core/cloudwatch-sink';
import type { CloudWatchSinkConfig } from '../core/cloudwatch-sink';

export type NewRelicCloudWatchSinkProps = CloudWatchSinkConfig;

/**
 * Client component that activates the optional CloudWatch sink.
 *
 * Mount it alongside NewRelicPageViewTracker inside the Suspense block in
 * providers.tsx. Once mounted, every addPageAction call (including custom
 * events fired via useNewRelicEvents.trackAction) is POSTed to the endpoint
 * in addition to being forwarded to the NR browser agent.
 *
 * The sink is a no-op until this component mounts, so it is safe to include
 * unconditionally — if the API route doesn't exist, the POST simply 404s and
 * is silently swallowed.
 *
 * @example
 * // In providers.tsx — limit to key journey events to avoid log noise:
 * const CW_EVENTS = ['form_step_submitted', 'broken_journey_resumed'] as const;
 *
 * <NewRelicCloudWatchSink endpoint="/api/log-event" filterEvents={CW_EVENTS} />
 */
export function NewRelicCloudWatchSink({
  endpoint,
  filterEvents,
}: NewRelicCloudWatchSinkProps): null {
  useEffect(() => {
    configureCloudWatchSink({ endpoint, filterEvents });
    // filterEvents is intentionally omitted from deps — callers should pass a
    // stable reference (module-level constant) to avoid unnecessary re-runs.
    // Re-running configureCloudWatchSink is idempotent so the worst case is a
    // redundant module-state write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  return null;
}
